import { FC, useEffect, useRef } from "react";
import { cloneDeep } from "lodash";
import { useFormContext } from "react-hook-form";
import { BeefOutputGrowing } from "../../../../models/Baseline/BeefBaseline";
import BeefOutputInputField from "./BeefOutputInputField";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { FormType } from "../../common";
import { unitLong } from "../../../../utils/unit-utils";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { StageType } from "../../../../../../graphql/types";

import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";

interface BeefOutputCowFormProps {
  fieldItemPrefix: string;
  outputBaselineData: BeefOutputGrowing;
  formType: FormType;
  stageType: StageType;
  isOutputEdit: boolean;
  setIsOutputEdit: (isEditing: boolean) => void;
}

const BeefOutputCowForm: FC<BeefOutputCowFormProps> = ({
  isOutputEdit = false,
  fieldItemPrefix,
  formType,
  outputBaselineData,
  stageType,
  setIsOutputEdit,
}) => {
  const fc = useFormContext<BeefIntervention>();
  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const outputMassUnit = unitLong(userUOM.unitOutputMass as string);
  const currResetValue = useRef<BeefOutputGrowing>();

  useEffect(() => {
    if (isOutputEdit) {
      currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOutputEdit]);

  const resetEdit = () => {
    fc.setValue(
      `${fieldItemPrefix}.cowsToAnotherStage`,
      currResetValue.current?.cowsToAnotherStage ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.averageWeightOfCowsLeavingTheStage`,
      currResetValue.current?.averageWeightOfCowsLeavingTheStage ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.cowsSold`,
      currResetValue.current?.cowsSold ?? ""
    );

    fc.setValue(
      `${fieldItemPrefix}.numberOfMortalitiesCows`,
      currResetValue.current?.numberOfMortalitiesCows ?? ""
    );
    fc.setValue(
      `${fieldItemPrefix}.averageWeightAtMortalityCows`,
      currResetValue.current?.averageWeightAtMortalityCows ?? ""
    );

    if (stageType === StageType.Growing) {
      fc.setValue(
        `${fieldItemPrefix}.averageAge`,
        currResetValue.current?.averageAge ?? ""
      );
    }

    setIsOutputEdit(false);
  };

  if (!outputBaselineData) {
    return null;
  }

  return (
    <>
      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="cowsToAnotherStage"
        originalKey="originalCowsToAnotherStage"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.COWS_TO_ANOTHER_STAGE"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.COWS_SOLD.PLACEHOLDER"
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.COWS_TO_ANOTHER_STAGE.TOOLTIP"
      />

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="averageWeightOfCowsLeavingTheStage"
        originalKey="originalAverageWeightLeaving"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.PLACEHOLDER"
        adornmentValues={{ unit: outputMassUnit }}
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.TOOLTIP"
      />

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="cowsSold"
        originalKey="originalCowsSold"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.COWS_SOLD"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.COWS_SOLD.PLACEHOLDER"
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.COWS_SOLD.TOOLTIP"
      />

      {stageType === StageType.Growing && (
        <BeefOutputInputField
          fieldItemPrefix={fieldItemPrefix}
          keyName="averageAge"
          originalKey="originalAverageAge"
          labelId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_AGE"
          editEnabled={isOutputEdit}
          outputData={outputBaselineData}
          adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_AGE.PLACEHOLDER"
          tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_AGE.TOOLTIP"
        />
      )}

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="numberOfMortalitiesCows"
        originalKey="originalNumberOfMortalities"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.NUMBER_OF_MORTALITIES_COWS"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.NUMBER_OF_MORTALITIES_COWS.PLACEHOLDER"
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.NUMBER_OF_MORTALITIES_COWS.TOOLTIP"
      />

      <BeefOutputInputField
        fieldItemPrefix={fieldItemPrefix}
        keyName="averageWeightAtMortalityCows"
        originalKey="originalAverageWeightMortality"
        labelId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS"
        editEnabled={isOutputEdit}
        outputData={outputBaselineData}
        adornmentId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS.PLACEHOLDER"
        adornmentValues={{ unit: outputMassUnit }}
        tooltipId="SUSTELL.STAGE.BEEF.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS.TOOLTIP"
      />

      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isOutputEdit}
          onEdit={() => setIsOutputEdit(true)}
          onCancel={resetEdit}
        />}
    </>
  );
};

export default BeefOutputCowForm;
